import {
	NextButton,
	PrevButton,
} from '@components/carousel-buttons/carousel-buttons';
import { FilmstripPhoto } from '@components/filmstrip-carousel/FilmstripPhoto';
import { useSlideWidth } from '@components/filmstrip-carousel/hooks/use-slide-width';
import useTrackUnifiedLightbox from '@components/unified-lightbox/hooks/use-track-unified-lightbox';
import AnalyticsConstants from '@constants/analytics/index.js';
import { useUgcStorefrontAnalytics } from '@containers/review-photos/hooks/use-ugc-storefront-analytics';
import { useAppSelector } from '@redux/hooks';
import { noopParameters } from '@utils/noop';
import { compose } from '@xo-union/react-css-modules';
import { Carousel } from '@xo-union/ui-carousel';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import type { Gallery } from 'types/reviews';
import Styles from './FilmstripCarousel.scss';

interface Props {
	galleryItems: Gallery[];
	gallerySize: number;
	onClick?: (index: number, modalType: 'overview' | 'details') => void;
	maxPhotos?: number;
}

export const FilmstripCarousel = ({
	galleryItems,
	gallerySize,
	onClick = noopParameters,
	maxPhotos = 8,
}: Props) => {
	const vendor = useAppSelector(
		(state) => state.vendor.vendor as Vendor.Decorated,
	);
	const slideWidth = useSlideWidth();
	const [carouselIndex, setCarouselIndex] = useState(0);
	const galleryItemsToDisplay = useMemo(
		() => galleryItems.slice(0, maxPhotos),
		[galleryItems, maxPhotos],
	);
	const numberOfExtraPhotos = useMemo(() => {
		return gallerySize - maxPhotos;
	}, [gallerySize, maxPhotos]);
	const trackUgcEvent = useUgcStorefrontAnalytics();
	const trackUnifiedLightbox = useTrackUnifiedLightbox(vendor);
	const onNavigationIndexChange = (nextIndex: number) => {
		const action =
			nextIndex > carouselIndex
				? AnalyticsConstants.NAV_RIGHT
				: AnalyticsConstants.NAV_LEFT;
		trackUgcEvent(action, AnalyticsConstants.REVIEW_GALLERY_FILMSTRIP);
		setCarouselIndex(nextIndex);
	};
	const onClickTile = (index: number) => {
		trackUgcEvent(
			AnalyticsConstants.PHOTO_VIEWED,
			AnalyticsConstants.REVIEW_GALLERY_FILMSTRIP,
			index + 1,
		);
		trackUnifiedLightbox(
			AnalyticsConstants.VENDOR_PORTFOLIO_INTERACTION,
			AnalyticsConstants.CLICKED_REVIEW_PHOTO,
			AnalyticsConstants.portfolioType.REVIEWS,
		);
		onClick(index, 'details');
	};
	const onClickSeeAllTile = () => {
		trackUgcEvent(
			AnalyticsConstants.SEE_ALL_IMAGES,
			AnalyticsConstants.portfolioType.REVIEWS,
		);
		trackUnifiedLightbox(
			AnalyticsConstants.VENDOR_PORTFOLIO_INTERACTION,
			AnalyticsConstants.SEE_ALL_IMAGES,
			AnalyticsConstants.portfolioType.REVIEWS,
		);
		onClick(0, 'overview');
	};

	const carouselStyles = compose({
		container: Styles.container,
		relativeContainer:
			galleryItemsToDisplay.length < 6
				? Styles.lessThanSix
				: Styles.relativeContainer,
	});
	return (
		<Carousel
			classes={carouselStyles}
			gutter={8}
			minSlideWidth={slideWidth}
			maxSlideWidth={slideWidth}
			renderNextButton={NextButton}
			renderPrevButton={PrevButton}
			onNavigationIndexChange={onNavigationIndexChange}
			navigationIndex={carouselIndex}
		>
			{galleryItemsToDisplay.map((item, index) => (
				<button
					aria-label="review photo"
					type="button"
					key={`filmstrip_carousel_${item.sourceId}`}
					data-testid={`filmstrip_carousel_${item.sourceId}`}
					className={classNames(Styles.mediaWrapper, Styles.mediaSize)}
					onClick={() => {
						onClickTile(index);
					}}
				>
					<FilmstripPhoto item={item} />
				</button>
			))}
			{numberOfExtraPhotos > 0 ? (
				<button
					aria-label="review photo"
					type="button"
					data-testid="filmstrip_carousel_extra_photos"
					className={classNames(Styles.mediaWrapper, Styles.mediaSize)}
					onClick={onClickSeeAllTile}
				>
					<div
						className={classNames(Styles.overlay, Styles.mediaSize)}
					>{`+${numberOfExtraPhotos}`}</div>
					<FilmstripPhoto item={galleryItems[8]} />
				</button>
			) : null}
		</Carousel>
	);
};
